<template>
  <div class="flex flex-col">
    <QuillEditor
      v-bind="$attrs"
      ref="editorComponent"
      v-model:content="content"
      theme="snow"
      :toolbar="toolbarToRender"
      :placeholder="!disabled ? placeholder : ''"
      :modules="mergedModules"
      :read-only="disabled"
      @text-change="handleTextChange"
      @update:content="handleContentUpdate"
      @selection-change="emit('selection:change')"
      @ready="emit('editor:ready')"
      @blur="emit('blur')"
      @focus="emit('focus')"
      @click="emit('click', $event)"
    >
      <template v-if="$slots.customToolbar" #toolbar>
        <div :id="`custom-toolbar--${name}`">
          <slot name="customToolbar" />
        </div>
      </template>
    </QuillEditor>
    <div
      v-if="errorMessage"
      class="pt-2 pl-2 text-left text-xxs text-red-700 sm:text-xs"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>
<script setup lang="ts">
import {
  type PropType,
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  watch,
} from "vue";

import { Delta, Quill, QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { useField } from "vee-validate";

import { ShortcutContext } from "@/enums/keyboardShortcuts";
import { WYSIWYG_TOOLBAR } from "@/helpers/constants";
import { moveFocus } from "@/helpers/quill";
import {
  convertSemanticToQuillHTML,
  convertToPureHTML,
} from "@/helpers/textEditor";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { type EditorModule, StyledMentionBlot } from "@/lib/Quill";

import type { Direction } from "@/helpers/quill";

/* eslint-disable @typescript-eslint/no-explicit-any */
const slots = defineSlots<{
  customToolbar?: (props: Record<string, never>) => any;
}>();
/* eslint-enable @typescript-eslint/no-explicit-any */

const props = defineProps({
  value: {
    type: [String, Delta],
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  toolbar: {
    type: [Object, Array],
    default: () => [...WYSIWYG_TOOLBAR],
  },
  modules: {
    type: Array as PropType<EditorModule[]>,
  },
  pureMarkup: {
    type: Boolean,
    default: false,
  },
  allowShortcuts: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  "update:content": [value: string];
  "selection:change": [void];
  "editor:ready": [void];
  blur: [void];
  focus: [void];
  click: [Event];
}>();

const { configuredShortcuts, registerQuillShortcuts } = useKeyboardShortcuts();

const editorComponent = ref<InstanceType<typeof QuillEditor> | null>(null);
const content = ref(
  props.pureMarkup && typeof props.value === "string"
    ? convertSemanticToQuillHTML(props.value)
    : (props.value ?? "")
);

let isInternalUpdate = false;

Quill.register(StyledMentionBlot);

const editorInstance = computed<Quill | null>(
  () => editorComponent.value?.getQuill() || null
);
const { errorMessage, handleChange } = useField(props.name, undefined);
const emitter =
  getCurrentInstance()?.appContext.config.globalProperties.emitter;

const insertText = (text: string) => {
  editorInstance.value?.insertText(editorInstance.value.getLength(), text);
};
const deleteText = (text: string) => {
  editorInstance.value?.deleteText(
    editorInstance.value.getText().indexOf(text),
    text.length + 1
  );
};

const handleTextChange = () => {
  if (props.pureMarkup) {
    const cleanHTML = convertToPureHTML(editorInstance.value);
    handleChange(cleanHTML);
  } else {
    handleChange(editorInstance.value?.root.innerHTML);
  }
};

const toolbarToRender = computed(() => {
  if (slots.customToolbar) {
    return `#custom-toolbar--${props.name}`;
  }

  return props.toolbar ?? WYSIWYG_TOOLBAR;
});

const createTabHandler = (direction: Direction) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (range: any, context: any) => {
    if (context.format && context.format.list) {
      return true;
    }
    moveFocus(direction);
    return false;
  };
};

const mergedModules = computed(() => {
  const localBindings = {
    tab: {
      key: 9,
      handler: createTabHandler(1),
    },
    shiftTab: {
      key: 9,
      shiftKey: true,
      handler: createTabHandler(-1),
    },
  };

  const propModules = props.modules || [];
  const propKeyboardModule = propModules.find((m) => m.name === "keyboard");
  const otherModules = propModules.filter((m) => m.name !== "keyboard");

  const keyboardBindings: EditorModule = {
    name: "keyboard",
    module: Quill.import("modules/keyboard"),
    options: {
      bindings: {
        ...localBindings,
        ...(propKeyboardModule?.options?.bindings ?? {}),
      },
    },
  };

  return [...otherModules, keyboardBindings];
});

const handleContentUpdate = async (content: string) => {
  isInternalUpdate = true;
  if (props.pureMarkup) {
    const cleanHTML = convertToPureHTML(editorInstance.value);
    emit("update:content", cleanHTML);
  } else {
    emit("update:content", content);
  }
  await nextTick();
  isInternalUpdate = false;
};

onMounted(() => {
  if (props.pureMarkup) {
    const cleanHTML = convertToPureHTML(editorInstance.value);
    handleChange(cleanHTML);
  } else {
    handleChange(editorInstance.value?.root.innerHTML);
  }
  emitter.on("insert-text", (text: string) => insertText(text));
  emitter.on("remove-text", (text: string) => deleteText(text));
  if (props.allowShortcuts && editorInstance.value) {
    const editorShortcuts = configuredShortcuts.value.filter(
      (shortcut) => shortcut.context === ShortcutContext.EmailSms
    );
    registerQuillShortcuts({
      quill: editorInstance.value,
      shortcuts: editorShortcuts,
    });
  }
});

watch(
  () => props.value,
  (newValue) => {
    if (isInternalUpdate) {
      return;
    }
    const newContent =
      props.pureMarkup && typeof newValue === "string"
        ? convertSemanticToQuillHTML(newValue)
        : newValue;

    if (newContent !== content.value) {
      content.value = newContent;
    }
  }
);

defineExpose({ editorInstance, content });
</script>

<style>
@reference "@/assets/tailwind.css";

.ql-container {
  @apply max-h-52;
  min-height: 130px !important;
}

.ql-editor {
  word-wrap: break-word !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  white-space: pre-wrap !important;
}
</style>
